// Telemetry for the "Save to list" dialog, in the apps' vocabulary so one PostHog question
// covers iOS, Android and the web (same event and property names, plus platform: 'web'):
//
//   save_to_list_opened        { source }
//
// Every event also carries `item`: 'hike' or 'recording', what the dialog was saving.
//   save_to_list_done          { added_count, removed_count, created_count }
//   save_to_list_search_used   — once per opening, on the first search
//   save_to_list_filter_added  — the Added pill
//   save_to_list_list_created
//   save_to_list_list_deleted  — once the Undo window has passed
//   save_to_list_delete_undone
//
// Usage goes through `window.mixpanel`, the facade that routes to PostHog. Failures
// (save_to_list_add_failed, _remove_failed, _create_failed, _delete_failed) go to Bugsnag
// as handled warnings, the web's equivalent of the apps' BetterStack failure log.

import { reportHandledError } from '../bugsnag';

export const SAVE_TO_LIST_EVENTS = {
  opened: 'save_to_list_opened',
  done: 'save_to_list_done',
  searchUsed: 'save_to_list_search_used',
  filterAdded: 'save_to_list_filter_added',
  listCreated: 'save_to_list_list_created',
  listDeleted: 'save_to_list_list_deleted',
  deleteUndone: 'save_to_list_delete_undone',
} as const;

export const SAVE_TO_LIST_FAILURES = {
  add: 'save_to_list_add_failed',
  remove: 'save_to_list_remove_failed',
  create: 'save_to_list_create_failed',
  delete: 'save_to_list_delete_failed',
} as const;

export type SaveToListFailure = typeof SAVE_TO_LIST_FAILURES[keyof typeof SAVE_TO_LIST_FAILURES];

/** `source` on save_to_list_opened, in the apps' words where the place is the same. */
export function saveToListSource(pathname: string): string {
  if (/^\/(user\/recordings|recordings\/users)\/[^/]+\/?$/.test(pathname)) return 'recording_overview';
  if (/^\/(trails|hikes)\/.+\/map\/?$/.test(pathname)) return 'hike_map';
  if (/^\/(trails|hikes)\/[^/]+/.test(pathname)) return 'hike_overview';
  if (/\/completed-hikes\/?$/.test(pathname)) return 'completed_hikes';
  return 'other';
}

export function doneProperties(counts: { added: number; removed: number; created: number }): Record<string, number> {
  return { added_count: counts.added, removed_count: counts.removed, created_count: counts.created };
}

/** Fire-and-forget; a missing analytics shim must never break the dialog. */
export function trackSaveToList(event: string, props: Record<string, unknown> = {}): void {
  try {
    (window as any).mixpanel?.track(event, { ...props, platform: 'web' });
  } catch {
    // Telemetry must never break the flow it is reporting on.
  }
}

export function reportSaveToListFailure(
  event: SaveToListFailure,
  { listId, hikeId, recordingId, error }: {
    listId?: string | number | null;
    hikeId?: string | number | null;
    recordingId?: string | number | null;
    error?: unknown;
  },
): void {
  const cause = error instanceof Error ? error.message : error == null ? event : String(error);
  reportHandledError(new Error(`${event}: ${cause}`), {
    component: 'save_to_list',
    operation: event,
    listId: listId ?? null,
    hikeId: hikeId ?? null,
    ...(recordingId != null ? { recordingId, item: 'recording' } : {}),
  });
}
